import React, { Children, cloneElement, forwardRef, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import TabsGroupContext from '../TabsGroup/TabsGroupContext'
import { createUseStyles } from 'react-jss'

import './tabs.scss'

/* @TODO:
 * - scroll to Tab when clicked and it is overflown
 * - if size is small enough to need scroll -> disabling fillSpace
 * - check why "onChange" doesnt execute
 * - isControlled
 * - default index-value if not setted
 * - add context flag to show shadow when panel has scrolled
 * - fix shadow
 * - propTypes
 */

const useStyles = createUseStyles(
	{
		styles: {
			borderTop: (props) => props.borderTop && '1px solid #ddd9d2',
			borderBottom: (props) => props.borderBottom && '1px solid #ddd9d2'
		}
	},
	{ name: 'Vb' }
)

const Tabs = forwardRef(function Tabs(props, ref) {
	const {
		'aria-label': ariaLabel,
		'aria-labelledby': ariaLabelledBy,
		centered = false,
		children,
		className,
		component: Component = 'div',
		fillSpace = false,
		onChange,
		orientation = 'horizontal',
		selectionFollowsFocus,
		value: valueProp,
		visibleScrollbar = false,
		tabListProps = {},
		...other
	} = props

	//--------------------------------------------------------
	/* Style */
	const classes = useStyles(props)

	//--------------------------------------------------------
	/* Variables */
	const TabsContext = useContext(TabsGroupContext)
	const [value, setValue] = useState(TabsContext.value)

	//--------------------------------------------------------
	/* Tab change */
	const handleItemClick = (child) => (event) => {
		let newValue = child.props.value

		if (child.props.onClick) {
			child.props.onClick(child)
		}
		if (value === newValue) {
			return
		}
		// setting Tab value to visualize selected one (css indicator)
		// setting Context value to perform panel change
		setValue(newValue)
		TabsContext.handleChange(event, newValue)

		if (onChange) {
			onChange(child, event)
		}
	}

	const handleChange = (event) => {
		if (onChange) {
			onChange(event)
		}
	}

	//--------------------------------------------------------
	/* Tabs index managment */

	// Map that stores the link between tab and its index.
	// i.e.: {key: 0, value: 'Home'}
	const valueToIndex = new Map()

	const childrenArray = Children.toArray(children)
	const items = childrenArray.map((child, i) => {
		if (!React.isValidElement(child)) {
			return null
		}

		// if Tab value (name) is not setted, use index ** LINK WITH PANELS NOT WORKING
		const childValue = child.props.value === undefined ? i : child.props.value
		valueToIndex.set(childValue, i)
		const selected = childValue === value

		return cloneElement(child, {
			fullWidth: fillSpace,
			onClick: handleItemClick(child),
			'aria-controls': TabsContext.getPanelId(),
			id: TabsContext.getTabId(),
			selected,
			selectionFollowsFocus,
			onChange,
			value: childValue,
			...(i === 0 && value === false && !child.props.tabIndex && { tabIndex: 0 })
		})
	})

	//--------------------------------------------------------
	/* Render */

	return (
		<Component
			className={clsx('Vb-tabs__wrapper', classes.styles, className)}
			ref={ref}
			value={value}
			onChange={handleChange}
			{...other}
		>
			<div
				className={clsx(
					!visibleScrollbar && 'Vb-tabs__scroller--no-scrollbar',
					'Vb-tabs__scroller--scrolleable', // originally choosable between not/scrollable
					'Vb-tabs__scroller'
				)}
			>
				{/* The tablist isn't interactive but the tabs are */}
				<div
					aria-label={ariaLabel}
					aria-labelledby={ariaLabelledBy}
					className={clsx('Vb-tabs__tablist', centered && 'Vb-tabs__tablist--centered')}
					role="tablist"
					{...tabListProps}
				>
					{items}
				</div>
			</div>
		</Component>
	)
})

export default Tabs
